import { useOutletContext } from "react-router"
import { PlaceOutletContextType } from "../home/PlacesHome"
import { useErrorBoundary } from "react-error-boundary"
import { useCallback, useContext, useEffect, useState } from "react"
import { NotificationContext } from "shared/NotificationContext"
import { AVAILABLE_RANGES, DateRange, NotificationType, PlaceDashboard } from "shared/topchef.types"
import api from 'shared/topchef-api-places';
import Loading from "pages/shared/loading/Loading"
import { formatAmount } from "shared/topchef.utils"
import SharedLeadsLoader from "../shared-leads-loader/SharedLeadsLoader"
import FollowedLeadsLoader from "../followed-leads-loader/FollowedLeadsLoader"
import LeadsToCloseLoader from "../leads-to-close-loader/LeadsToCloseLoader"
import { useTranslation } from "react-i18next";

const PlaceDashboardComponent = () => {
  const { t } = useTranslation();

  const [loggedInPlace] = useOutletContext<PlaceOutletContextType>();
  
  const [currentRange, setCurrentRange] = useState<DateRange>(AVAILABLE_RANGES[0])
  const [dashboardReady, setDashboardReady] = useState<boolean>(false);
  const [dashboard, setDashboard] = useState<PlaceDashboard | null>(null);
  
  const { showBoundary } = useErrorBoundary();
  const notificationContext = useContext(NotificationContext);
  const { triggerNotification } = notificationContext;
  

  const loadDashboard = useCallback( async () => { // eslint-disable-line react-hooks/exhaustive-deps
    setDashboardReady(false);
    try {
      const currentDashboard: PlaceDashboard = await api.loadDashboard(loggedInPlace!.id, currentRange);
      setDashboard(currentDashboard);

      setDashboardReady(true);
    } catch (error: any) { 
      const newError = new Error(t('apiErrors.loadDashboard', { details: error.message }) as string);
      triggerNotification({type: NotificationType.error, message : newError.message});
      return showBoundary(newError);
    } 
  }, [currentRange, loggedInPlace, triggerNotification, showBoundary]); // eslint-disable-line react-hooks/exhaustive-deps

  

  useEffect(() => {
    loadDashboard();
  }, [currentRange, loadDashboard,loggedInPlace, triggerNotification, showBoundary]);

  const changeRange = (event: React.ChangeEvent<HTMLSelectElement> ) => {
    event.preventDefault();
    const selectedValue = event.target.value;
    const selectedRange: DateRange =  AVAILABLE_RANGES.find(range => range.value === selectedValue) || {value: '', label: '', startDate : null, endDate: null}
    setCurrentRange(selectedRange);
  };


  if (!dashboardReady) {
    return (
      <Loading />
    );
  }


  return (
    <>
      <div className="page-dashboard container p-5 overflow-auto vh-100">
          <div className="dashboard-head row">
              <div id="lead_sidebar" className="d-flex">
                <ul className="nav nav-pills flex-column mb-auto">
                  <li className="nav-item"> <a href="/places/share-a-lead" type="button" className="btn btn-outline-primary nav-link add-brief-link">{t('common.addBrief')}</a></li>
                </ul>
              </div>
              <h4 className="mb-3 col-md-9">{t('dashboard.yourDashboard')}</h4>
              <div className="lochall_points col-md-3 text-end">     
                  <span className="fw-bold">{t('dashboard.yourPoints')}{loggedInPlace!.locHallPoints}</span>
                  <span className="lochall_points_count" ></span>
              </div>
          </div>
          <div className="row g-4">
              <div className="col-md-12">
                  <select name="global_traffic_view" onChange={changeRange} value={currentRange.value}>
                    {AVAILABLE_RANGES.map((range: DateRange) => (
                        <option key={range.value} value={range.value}>
                          {t(range.label)}
                        </option>
                      ))}
                  </select>
              </div>
              <div className="col-md-2 w-20">
                  <div id="briefs_moving" className="card p-3 text-center">
                    <p className="fw-bold mb-2">{t('dashboard.totalBriefsInCirculation')}</p>
                      <h3 id="briefs_moving_count" className="mb-0">{dashboard!.briefMarketSize}</h3>
                  </div>
              </div>
              <div className="col-md-2 w-20">
                  <div id="briefs" className="card p-3 text-center">
                    <p className="fw-bold mb-2">{t('dashboard.sharedBriefsCount')}</p>
                      <h3 id="briefs_count" className="mb-0">{dashboard!.sharedLeads}</h3>
                  </div>
              </div>
              <div className="col-md-2 w-20">
                  <div id="briefs_signed" className="card p-3 text-center">
                    <p className="fw-bold mb-2">{t('dashboard.signedBriefsCount')}</p>
                      <h3 id="briefs_signed_count" className="mb-0">{dashboard!.signedLeads}</h3>
                  </div>
              </div>
              
              <div className="col-md-2 w-20">
                  <div id="event_gains" className="card p-3 text-center">
                    <p className="fw-bold mb-2">{t('dashboard.earnings')}</p>
                      <h3 id="event_gains_count" className="mb-0">{formatAmount(dashboard!.earnedFees)}</h3>
                  </div>
              </div>

          </div>
      
        <div className="mt-4">
          <SharedLeadsLoader
            title={t('dashboard.last3SharedBriefs')}
            showMenu={false}
            limit={3}
            paginateResults={false} 
            height="200px" />
        </div>

        {loggedInPlace.acceptReward &&
          <>
            <div className="mt-4">
              <FollowedLeadsLoader
                title={t('dashboard.last3BriefsToProcess')}
                showMenu={false}
                limit={3}
                paginateResults={false} 
                height="200px" />
            </div>

            <div className="mt-4 mb-4">
              <LeadsToCloseLoader 
                title={t('dashboard.last3SignedBriefs')}
                showMenu={false}
                limit={3}
                paginateResults={false} 
                height='200px'
                />
            </div>
          </>
        }
        {loggedInPlace.acceptReward === false &&
          <>
            <div className="mt-4">
              <FollowedLeadsLoader
                title={t('dashboard.last3FollowedBriefs')}
                showMenu={false}
                limit={3}
                paginateResults={false} 
                height="200px" />
            </div>
          </>
        }

      </div>

    </>
  );

}

export default PlaceDashboardComponent;
